import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TextInput } from '../../Form';
import { FormFieldItem } from './FormFieldItem';
import Label from './Label';
import StyledElement from '@repo/checkout/components/StyledElement';
import useCheckoutState from '../../../hooks/useCheckoutState';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import resolveInheritedEnabled from '../../../lib/resolveInheritedEnabled';
import clsx from 'clsx';

const PayWhatYouWantField = ({
  payWhatYouWantText,
  placeholder,
  currency,
  suffix,
  value,
  onChange,
  onBlur,
  hideLabel = false,
  innerRef,
  inputClassName,
  suffixElementPath = 'checkout.pwyw.suffix',
}) => {
  const { theme } = useCheckoutState();
  const rightText = suffix || (currency && currency.toUpperCase());
  // The Interval / Currency element gates the suffix per context. The span keeps
  // its ref (used to measure width for the input padding), so we read the toggle
  // directly rather than wrapping it in a StyledElement (which doesn't forward refs).
  const suffixEnabled = resolveInheritedEnabled(
    theme?.elements,
    suffixElementPath,
    'checkout.pwyw.suffix',
  );
  const node = useRef();
  const suffixRef = useRef(null);

  // Stable so the input's ref isn't detached/reattached on every render.
  const setInputNode = useCallback(
    el => {
      node.current = el;
      if (typeof innerRef === 'function') innerRef(el);
      else if (innerRef) innerRef.current = el;
    },
    [innerRef],
  );

  // A recurring PWYW price frames the input as a rate by rendering the billing
  // interval as a suffix (e.g. "/month"); pad the input's right edge by the
  // measured suffix width so a long amount never runs under it. The plain
  // currency-code suffix (one-time) floats without padding, as before.
  const [suffixWidth, setSuffixWidth] = useState(null);
  useIsomorphicLayoutEffect(() => {
    setSuffixWidth(suffix && suffixRef.current ? suffixRef.current.offsetWidth : null);
  }, [suffix]);

  // This is a hack to prevent the input from scrolling the page when the user
  // scrolls the mouse wheel over the input.
  useEffect(() => {
    const handleScroll = () => {
      node.current.blur();
    };

    if (node.current) {
      node.current.addEventListener('wheel', handleScroll, { passive: true });
    }
  }, [node]);

  return (
    <FormFieldItem className="cp_form-item">
      {!hideLabel && payWhatYouWantText && (
        <StyledElement
          as={Label}
          elementPath="checkout.pwyw.label"
          htmlFor="payWhatYouWant"
          className={clsx(
            'cp_pwyw-label',
            'break-words',
            '[color:var(--cp_pwyw-label-color,var(--cp_checkout-form-fields-label-color))]',
            '[font-family:var(--cp_pwyw-label-font-family,var(--cp_checkout-form-fields-label-font-family))]',
            '[font-style:var(--cp_pwyw-label-font-style,var(--cp_checkout-form-fields-label-font-style))]',
            '[font-weight:var(--cp_pwyw-label-font-weight,var(--cp_checkout-form-fields-label-font-weight))]',
            '[font-size:var(--cp_pwyw-label-font-size,var(--cp_checkout-form-fields-label-font-size))]',
            '[line-height:var(--cp_pwyw-label-line-height,var(--cp_checkout-form-fields-label-line-height))]',
            '[padding-bottom:var(--cp_pwyw-label-padding-bottom,var(--cp_checkout-form-fields-label-padding-bottom))]',
          )}
        >
          {payWhatYouWantText}
        </StyledElement>
      )}

      <div className={clsx('cp_paywhatyouwant-input relative w-full')}>
        <TextInput
          innerRef={setInputNode}
          className={clsx('cp_form-textinput w-full', inputClassName)}
          style={
            suffix
              ? {
                  paddingRight: `calc(var(--cp_price-picker-option-pwywInput-padding-x) + ${
                    suffixWidth ?? 30
                  }px + 0.4em)`,
                }
              : undefined
          }
          id="payWhatYouWant"
          name="payWhatYouWant"
          type="number"
          placeholder={placeholder}
          value={value || ''}
          onKeyDown={e => {
            if (e.key === 'e' || e.key === 'E') {
              e.preventDefault();
            }
          }}
          onChange={e => {
            const value = e.target.value;

            // Allow numbers, a single dot or comma for decimal, and no more than two decimals
            const newValue = value
              .replace(/[^\d.,]+/, '') // Remove any character that is not a digit, a period, or a comma
              .replace(/(\.\d{2})\d+|(\,\d{2})\d+/, '$1$2') // Allow only two digits after the dot or comma
              .replace(/(\d+)([.,])(\d{2}).*/, '$1$2$3'); // Remove any characters after two decimals

            onChange(newValue);
          }}
          onBlur={onBlur}
        />
        {rightText && suffixEnabled && (
          <span
            ref={suffixRef}
            className="cp_paywhatyouwant-currency pointer-events-none absolute top-1/2 -translate-y-1/2"
          >
            {rightText}
          </span>
        )}
      </div>
    </FormFieldItem>
  );
};

export default PayWhatYouWantField;
